import React, {useCallback, useEffect, useState} from 'react';
import {API_URL} from '../../../constants';
import {fetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import {findIndex} from 'lodash';
import {loadingState} from '../../../recoil/bidmiiCustomLoader/bidmiiCustomLoaderAtom';
import {useSetRecoilState} from 'recoil';
import {Grid, Typography} from '@mui/material';
import {isVideo} from '../../../util/VideoHelpers';
import * as Sentry from '@sentry/react';

interface IProps {
  project: any;
  handleClose: any;
}

export default function ImageLightbox({project, handleClose}: IProps) {
  const setGlobalLoading = useSetRecoilState(loadingState);

  const [images, setImages] = useState<any[]>([]);
  const [photoIndex, setPhotoIndex] = useState(0);

  const getProjectImages = useCallback(() => {
    setGlobalLoading(true);
    const url = `${API_URL}/rfps/${project.uid}`;
    return fetchRequest(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) => res.json())
      .then((res) => {
        setGlobalLoading(false);
        if (res.attachmentReferences) {
          const filteredRefs = res.attachmentReferences
            .filter((ref: any) =>
              ['before_image', 'after_image'].includes(ref.key),
            )
            .sort((ref: any) => (ref.key === 'before_image' ? -1 : 1));
          if (filteredRefs.length === 0) {
            handleClose();
          }
          setImages(filteredRefs);

          const initialIndex = findIndex(
            filteredRefs,
            (ref: any) => ref.url === project.images.originalImageUrl,
          );
          setPhotoIndex(initialIndex);
        }
      })
      .catch((err) => {
        setGlobalLoading(false);
        process.env.NODE_ENV === 'development' &&
          console.log(err, 'got an error');
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'ImageLightbox.tsx getProjectImages()');
          Sentry.captureException(err);
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (project.reviewType !== 'CONTRACTOR HISTORY') {
      getProjectImages();
    } else {
      const filteredRefs = project.attachmentReferences;
      if (filteredRefs.length === 0) {
        handleClose();
      }
      setImages(filteredRefs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getProjectImages]);

  const getImageCaption = () => {
    return (
      <Grid container alignItems="center">
        <Typography variant="body2">
          {images[photoIndex]?.description.replace(' image', '') ||
            images[photoIndex]?.name.replace(' image', '')}
        </Typography>
        {project.images && (
          <>
            &nbsp;&nbsp;{'\u2022'}&nbsp;&nbsp;
            <a
              href={`https://bidmii.com/communityrfps/${project.uid}`}
              target="_blank"
              style={{color: '#FFF'}}>
              See project on Bidmii
            </a>
          </>
        )}
      </Grid>
    );
  };

  if (images.length === 0) {
    return null;
  }

  return (
    <Lightbox
      mainSrc={images[photoIndex]?.url}
      nextSrc={images[(photoIndex + 1) % images.length]?.url}
      prevSrc={images[(photoIndex + images.length - 1) % images.length]?.url}
      onCloseRequest={handleClose}
      onMovePrevRequest={() =>
        setPhotoIndex((photoIndex + images.length - 1) % images.length)
      }
      onMoveNextRequest={() =>
        setPhotoIndex((photoIndex + images.length + 1) % images.length)
      }
      imageTitle={`${photoIndex + 1} / ${images.length}`}
      imageCaption={getImageCaption()}
      reactModalStyle={{overlay: {zIndex: 20000}}}
      imageLoadErrorMessage={
        isVideo(images[photoIndex]?.url) ? (
          <video
            src={images[photoIndex]?.url}
            width="100%"
            controls
            autoPlay
            style={{height: '85vh'}}>
            Sorry, your browser doesn't support embedded videos
          </video>
        ) : (
          'This image failed to load'
        )
      }
    />
  );
}
